import { css } from "@emotion/react";
import { useContext } from "react";
import dayjs from "dayjs";
import { useTheme } from "../theme";
import { LocaleContext } from "../LocaleProvider";
import {
  WeeklyCalendarColumn,
  WeeklyCalendarColumnType,
  WeeklyCalendarRenderColumnProps,
} from "./WeeklyCalendarColumn";

/***
 * T以外のプロパティは互換性及びWeeklyCalendarColumnで利用する際に冗長なpropsの受け渡しを防ぐためにOptionalにしている。
 */
export type WeeklyCalendarRenderCellProps<T> = {
  date?: Date;
  dateStr?: string;
  isToday?: boolean;
  index?: number;
} & T;

/**
 * @deprecated use WeeklyCalendarRenderCellProps instead.
 */
export type WeeklyCalendarRenderCell<T> = (
  props: WeeklyCalendarRenderCellProps<T>,
) => React.JSX.Element;

export interface WeeklyCalendarRenderDateProps {
  date: Date;
  isToday: boolean;
}

export interface WeeklyCalendarProps<T> {
  /**
   * 各日付毎に入れるデータを指定する。
   * 日付を表すYYYYMMDDフォーマットのキーに対して、オブジェクトの配列を渡す。このオブジェクトはrenderCellに引数として渡る。
   */
  dates?: Record<string, T[]>;

  /**
   * datesで使用されたオブジェクトを引数として受け取り、どのように表示するかJSXで記述する。
   */
  renderCell: (props: WeeklyCalendarRenderCellProps<T>) => React.JSX.Element;

  /**
   * 最初に表示する日付を指定する。
   */
  date?: dayjs.Dayjs;

  /**
   * 列の表示部分を別のコンポーネントに切り替えられます。
   * childrenからは各セルが配列で渡されます。
   */
  renderColumn?: (
    props: WeeklyCalendarRenderColumnProps<T>,
  ) => React.JSX.Element;

  /**
   * 日付の表示部分を別のコンポーネントに切り替えられます。
   */
  renderDate?: (props: WeeklyCalendarRenderDateProps) => React.JSX.Element;

  /**
   * カラムの高さを指定する。
   * @default "auto"
   */
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  columnHeight?: number | string | "auto";
}

const weekNumbers = [0, 1, 2, 3, 4, 5, 6];

const DefaultRenderDate = ({
  date,
  isToday,
}: WeeklyCalendarRenderDateProps) => {
  const locale = useContext(LocaleContext);
  const theme = useTheme();
  const weekday = date.toLocaleDateString(locale, {
    weekday: "short",
  });
  return (
    <div
      key={date.getDate()}
      css={[
        theme.typography.h4,
        css`
          color: ${isToday
            ? theme.palette.intentions.primary.main
            : theme.palette.text.primary};
          text-align: center;
        `,
      ]}
    >
      <div
        css={css`
          margin-bottom: 1ex;
        `}
      >
        {weekday}
      </div>
      <div>{date.getDate()}</div>
    </div>
  );
};

export const WeeklyCalendar = <T,>({
  date = dayjs(),
  dates,
  renderCell: RenderCell,
  renderDate: RenderDate = DefaultRenderDate,
  renderColumn: RenderColumn = WeeklyCalendarColumn,
  columnHeight = "auto",
}: WeeklyCalendarProps<T>) => {
  const today = dayjs();
  const theme = useTheme();

  const columns: WeeklyCalendarColumnType<T>[] = weekNumbers.map((index) => {
    const day = date.add(index, "day");
    const isToday = day.isSame(today, "date");
    const dateStr = day.format("YYYYMMDD");
    return {
      date: day.toDate(),
      dateStr,
      isToday,
      index,
      rows: dates?.[dateStr] ?? [],
      renderCell: RenderCell,
    };
  });

  return (
    <div
      css={css`
        display: grid;
        grid-template-rows: auto ${typeof columnHeight === "number"
            ? `${columnHeight}px`
            : columnHeight};
        grid-template-columns: repeat(7, 1fr);
      `}
    >
      <div
        css={css`
          display: grid;
          grid-template-columns: subgrid;
          grid-column: span 7;
        `}
      >
        {columns.map((props, i) => (
          <div
            key={i}
            css={css`
              margin-bottom: ${theme.spacing * 2}px;
            `}
          >
            <RenderDate {...props} />
          </div>
        ))}
      </div>
      <div
        css={css`
          position: relative;
          display: grid;
          grid-template-columns: subgrid;
          grid-column: span 7;
          overflow-y: auto;
          border-top: 1px solid #e2e2e2;
          border-bottom: 1px solid #e2e2e2;
        `}
      >
        {columns.map((column) => {
          return (
            <RenderColumn key={column.dateStr} {...column}>
              {dates?.[column.dateStr]?.map((props, index) => {
                return (
                  // @ts-expect-error @emotion/reactの変更により型安全でなくなった https://github.com/emotion-js/emotion/compare/%40emotion/react%4011.13.0...%40emotion/react%4011.13.3#diff-a3e371cc2c719626107c869dcf5a0fad82d9ea99d37c727c96b3baf3c016fffdL89-R91*
                  <RenderCell
                    key={index}
                    index={index}
                    date={column.date}
                    dateStr={column.dateStr}
                    isToday={column.isToday}
                    {...props}
                  />
                );
              })}
            </RenderColumn>
          );
        })}
      </div>
    </div>
  );
};
